// File: modals/ReturnDeviceForm.jsx
"use client"

import { useState, useEffect, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { returnDevicesToWarehouse } from "../../../services/inventory/movement"
import Button from "../modules/Button/Button"
import Modal from "../modules/Modal/Moadal"
import Input from "../modules/Input/Input"
import Checkbox from "../modules/Checkbox/Checkbox"
import toast from "react-hot-toast"
import TextArea from "../modules/TextArea/TextArea"
import styles from "../../../styles/inventory/MoveSupplyForm.module.css"
import { parseDateSafe } from "../../../utils/dateFormatters"

const formatServerError = (data) => {
  if (!data && data !== "") return "Unknown error"
  if (typeof data === "string") return data
  if (typeof data === "object") return data.message || JSON.stringify(data)
  return String(data)
}

const ReturnDeviceForm = ({ devices = [], isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient()
  const [selectedDevices, setSelectedDevices] = useState(devices || [])
  const [notes, setNotes] = useState("")
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [submitting, setSubmitting] = useState(false)
  const [pendingPickup, setPendingPickup] = useState(false) // NEW: pending pickup flag

  useEffect(() => {
    if (devices && devices.length > 0) {
      setSelectedDevices(devices)
      const first = devices[0]
      setSelectedDate(first.moved_at ? (parseDateSafe(first.moved_at) || new Date()) : new Date())
      // if any device has a pending_pickup flag set, reflect it
      const anyPending = devices.some((d) => Boolean(d.pending_pickup))
      setPendingPickup(Boolean(anyPending))
    } else {
      setSelectedDevices([])
      setPendingPickup(false)
    }
  }, [devices])

  useEffect(() => {
    if (!isOpen) {
      setNotes("")
      setSubmitting(false)
      setSelectedDevices([])
      setPendingPickup(false)
    }
  }, [isOpen])

  const isFormValid = selectedDevices.length > 0

  const updateIndividualDevicesCache = useCallback(
    (movedDeviceIds = [], payload) => {
      if (!Array.isArray(movedDeviceIds) || movedDeviceIds.length === 0) return
      try {
        queryClient.invalidateQueries(["individualDevices"], { exact: false })
      } catch (err) {
        console.error("Error updating cache after return:", err)
      }
    },
    [queryClient],
  )

  const handleSubmit = async (e) => {
    e && e.preventDefault()
    if (!isFormValid || submitting) return
    setSubmitting(true)
    try {
      const deviceIds = selectedDevices
        .map((d) => (d.device_ids ? d.device_ids[0] : (d.original?.id || d.id)))
        .filter(Boolean)

      const moved_at = selectedDate.toISOString()

      const location_id =
        selectedDevices[0]?.current_location_id ||
        selectedDevices[0]?.original?.current_location_id ||
        selectedDevices[0]?.original?.location_id ||
        null

      const payload = {
        device_ids: deviceIds,
        moved_at,
        location_id: location_id ? Number(location_id) : null,
        notes: notes || "",
        is_picked: Boolean(pendingPickup), 
      }

      const res = await returnDevicesToWarehouse(payload)
      const success = res && (res.status === 200 || res.status === 201 || res.data?.success)

      if (success) {
        toast.success("Return successful")
        updateIndividualDevicesCache(deviceIds, payload)
        if (typeof onSuccess === "function") await onSuccess()
        setSelectedDevices([])
        onClose && onClose()
      } else {
        toast.error(formatServerError(res?.data ?? res))
      }
    } catch (err) {
      toast.error(formatServerError(err?.response?.data ?? err?.message))
    } finally {
      setSubmitting(false)
    }
  }

  if (!selectedDevices || selectedDevices.length === 0) {
    return (
      <Modal title="Return Device" isOpen={isOpen} onClose={onClose} width="700px" height="400px">
        <div className={styles.move_device_form}>
          <div className={styles.empty_state}>
            <p>No device selected for return.</p>
            <Button variant="secondary" size="small" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  const first = selectedDevices[0]
  const prefilledDeviceId = first.device_ids ? first.device_ids[0] : (first.original?.id || first.id)
  const prefilledMovedAt = first.moved_at || new Date().toISOString()
  const prefilledLocationId = first.location_id || first.original?.current_location_id || first.original?.location_id || null

  const movedAtDisplay = (() => {
    try {
      const d = new Date(prefilledMovedAt)
      return d.toISOString().split("T")[0] // only date
    } catch {
      return String(prefilledMovedAt)
    }
  })()

  return (
    <Modal
      title={`Return ${selectedDevices.length} Device${selectedDevices.length !== 1 ? "s" : ""}`}
      isOpen={isOpen}
      onClose={onClose}
      width="700px"
      height="auto"
      footer={
        <div className={styles.button_container}>
          <Button type="button" variant="secondary" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="small" disabled={!isFormValid || submitting} onClick={handleSubmit}>
            {submitting ? "Returning..." : `Return ${selectedDevices.length} Device${selectedDevices.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      }
    >
      <div className={styles.move_device_form}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label className={styles.label}>Device ID</label>
              <Input value={String(prefilledDeviceId || "")} readOnly fullWidth />
            </div>

            <div>
              <label className={styles.label}>Moved At </label>
              {/* Read-only display of date — not clickable */}
              <Input value={movedAtDisplay} readOnly fullWidth />
            </div>

            <div>
              <TextArea
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Returned after patient discharge"
                rows={5}
              />
            </div>

            {/* NEW: Pending pickup checkbox */}
            <div>
              <Checkbox
                checked={pendingPickup}
                onChange={(v) => setPendingPickup(Boolean(v))}
                label="Pending pickup (mark if device should be picked up later)"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default ReturnDeviceForm
